/**
 * One certification, as a card.
 *
 * ── Image first, because the certificate is the evidence ──────────────────────────────────
 *
 * This replaced a row in an eight-column table. The columns are all still here and in the same
 * words — employee, provider, date, result, expiry, the reimbursement tag, the retake deadline —
 * but ordered by what a reader is looking for rather than by what fits a header row: the
 * certificate itself, then what it is, then whose it is, then the details.
 *
 * ── The image is a thumbnail of a real document, so it has three states ───────────────────
 *
 * A certificate is uploaded as a PDF or as an image, and neither is guaranteed to be there:
 *
 *   a picture     shown, cropped to a fixed box so cards line up. A PDF reaches this state too:
 *                 the caller hands over a JPEG of page one, rendered at upload or in the browser,
 *                 so this file no longer knows or cares which kind of document it came from
 *   being made    a shimmering band, for the second or two a PDF with no stored thumbnail takes
 *                 to download and rasterise. Not the placeholder: the certificate IS there
 *   nothing       a placeholder saying so, which is different from a document that failed
 *
 * All three occupy the SAME height. That is what keeps a row of cards aligned, and it is why the
 * placeholder is a real panel rather than a collapsed empty div.
 *
 * `onError` collapses a fourth state into the last: a signed URL that expired mid-session, or a
 * key pointing at nothing, degrades to the placeholder instead of a broken icon.
 *
 * ── Nothing here decides anything ─────────────────────────────────────────────────────────
 *
 * Every rule this card renders — who may record a retake, whether a retake is overdue, whether a
 * certification has lapsed — is computed by the caller or by `types/certification`. This file
 * chooses words and layout and nothing else, so the register's logic has exactly one home.
 */

import React, { useEffect, useState } from 'react';
import {
  Certification,
  CertificationResult,
  certificationValidity,
  daysUntilExpiry,
  isRetakeOverdue,
} from '../../types/certification';

export interface CertificationCardProps {
  certification: Certification;
  /** Today as `YYYY-MM-DD`. Passed in so a grid of cards cannot disagree about the date. */
  today: string;
  /** Whether the employee's name and id are shown. Mirrors the page's own visibility rule. */
  showEmployee: boolean;
  /** Resolved thumbnail URL, or null when there is nothing to show. */
  previewUrl: string | null;
  /** Whether one is still being made — a PDF being rasterised. Shimmer rather than placeholder. */
  previewPending?: boolean;
  /** Whether the certification's own document is being opened right now. */
  opening: boolean;
  /** Whether this viewer may record a retake against this record. */
  canUpdate: boolean;
  formatDate: (iso?: string) => string;
  onOpenDocument: (certification: Certification) => void;
  onRecordAttempt: (certification: Certification) => void;
  /** A preview URL that failed to load, so the placeholder takes over. */
  onPreviewFailed: (certification: Certification) => void;
}

/** The placeholder mark. Inline because two icons are not worth a dependency or a sprite. */
const DocumentGlyph: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M14 3v5h5" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M19 8.5V19a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6.5L19 8.5Z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CertificationCard: React.FC<CertificationCardProps> = ({
  certification: c,
  today,
  showEmployee,
  previewUrl,
  previewPending = false,
  opening,
  canUpdate,
  formatDate,
  onOpenDocument,
  onRecordAttempt,
  onPreviewFailed,
}) => {
  /*
   * Has the thumbnail actually painted?
   *
   * The band shimmers until it has. A certificate is a photo somebody took on a phone, so even a
   * downscaled one arrives over the network — and an empty grey rectangle for those seconds reads
   * as a card with no certificate, which is the one thing it is not. The flag is reset when the
   * URL changes, because a fallback from the thumbnail to the original is a second load.
   */
  const [mediaLoaded, setMediaLoaded] = useState(false);
  useEffect(() => setMediaLoaded(false), [previewUrl]);

  const passed = c.result === CertificationResult.PASSED;
  const validity = certificationValidity(c, today);
  const expiryDays = daysUntilExpiry(c.expiryDate, today);
  const overdue = isRetakeOverdue(c, today);
  const hasDocument = Boolean(c.documentKey);

  /*
   * The image doubles as the button that opens the full certificate, because a thumbnail of a
   * document is the thing a reader expects to click. It is a <button> rather than an onClick on
   * the <img> so it is reachable by keyboard and announced as an action; the visible "View
   * Certificate" button below stays regardless, since a cropped thumbnail is not an obvious
   * affordance on its own.
   */
  const media =
    previewUrl ? (
      <button
        type="button"
        className={`certs__card-media${mediaLoaded ? ' is-loaded' : ' is-loading'}`}
        onClick={() => onOpenDocument(c)}
        disabled={opening}
        aria-label={`Open the certificate for ${c.name}`}
      >
        {/*
          * lazy + async + low priority, all three deliberate.
          *
          * `loading="lazy"` keeps a long register from fetching documents nobody scrolled to;
          * `decoding="async"` keeps decoding a multi-megapixel photo off the paint of the text
          * beside it; and the low fetch priority puts these behind the page's own data requests,
          * which is the right order when the image is a preview of something a click opens in
          * full anyway.
          */}
        <img
          src={previewUrl}
          alt={`Certificate for ${c.name}`}
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          onLoad={() => setMediaLoaded(true)}
          onError={() => onPreviewFailed(c)}
        />
      </button>
    ) : previewPending ? (
      /*
       * A PDF being rasterised. The same shimmer the <img> band uses while it loads, and for the
       * same reason: the certificate exists and is on its way, so the one thing this must not
       * show is the placeholder that says no certificate is there.
       */
      <div
        className="certs__card-media is-loading"
        role="img"
        aria-label={`Preparing the certificate preview for ${c.name}`}
      />
    ) : (
      <div className="certs__card-media certs__card-media--empty" aria-hidden="true">
        <DocumentGlyph />
        {/*
          * The filename used to be printed here, and for a PDF that was the whole preview. It is
          * gone deliberately: a document name told a reader nothing the heading below did not
          * already, and now that page one is rendered there is no document this band cannot show
          * on purpose. What is left are the two states that are genuinely empty, and they stay
          * distinguishable — a record with nothing attached is not a certificate whose preview
          * failed, and a reader who cannot see why has no way to tell which they are looking at.
          */}
        <span>{hasDocument ? 'Preview unavailable' : 'No certificate uploaded'}</span>
      </div>
    );

  return (
    <article className="certs__card">
      {media}

      <div className="certs__card-body">
        <div className="certs__card-title">
          <h3>{c.name}</h3>
          {/*
            * No source tag.
            *
            * A "reimbursement" badge used to sit here, on the reasoning that a record with an
            * approval history behind it is worth marking. On THIS page it says the wrong thing:
            * the register's own subtitle is "Adding one here is a record of a qualification — it
            * is not a reimbursement request", and a card badged REIMBURSEMENT next to that reads
            * as a claim rather than as where the row happened to come from.
            *
            * Nothing is lost. The badge was provenance, not status, and provenance belongs to
            * the reimbursement queue — which shows the claim, its stages and its decision in
            * full. `source` is still on the record for anything that needs to ask.
            */}
          <div className="certs__card-tags">
            {c.attempts.length > 1 && (
              <span className="certs__tag certs__tag--muted">{c.attempts.length} attempts</span>
            )}
          </div>
        </div>

        {showEmployee && (
          <div className="certs__card-person">
            <span className="certs__card-label">Employee</span>
            <span className="certs__who">{c.employeeName || c.employeeEmail}</span>
            {c.employeeId && <span className="certs__who-id">{c.employeeId}</span>}
          </div>
        )}

        <dl className="certs__card-facts">
          <div>
            <dt>Provider</dt>
            <dd>{c.provider}</dd>
          </div>
          <div>
            <dt>Date</dt>
            <dd>{formatDate(c.resultDate)}</dd>
          </div>
        </dl>

        <div className="certs__card-result">
          <span className={`certs__pill certs__pill--${passed ? 'passed' : 'failed'}`}>{c.result}</span>
          {/*
            * A failed certification says what has to happen next, not just that it failed. The
            * deadline is the actionable half, and an overdue one is called out rather than left
            * for the reader to compare against today.
            */}
          {!passed && c.retakeDeadline && (
            <span className={`certs__retake${overdue ? ' is-overdue' : ''}`}>
              Retake by {formatDate(c.retakeDeadline)}
              {overdue ? ' — overdue' : ''}
            </span>
          )}
          {passed && c.completionDate && (
            <span className="certs__completed">Completed {formatDate(c.completionDate)}</span>
          )}
        </div>

        <div className="certs__card-expiry">
          <span className="certs__card-label">Expires</span>
          {/*
            * Validity, not just the date. "2027-04-01" alone makes every reader do the
            * arithmetic, and the two cases worth acting on — lapsed, and lapsing soon — are
            * exactly the ones a bare date hides. A blank expiry is stated as "Does not expire"
            * rather than left empty, which would read as missing data.
            */}
          {validity === 'permanent' ? (
            <span className="certs__muted">Does not expire</span>
          ) : (
            <span className={`certs__expiry is-${validity}`}>
              {formatDate(c.expiryDate)}
              <span className="certs__expiry-note">
                {validity === 'expired'
                  ? `Expired ${Math.abs(expiryDays ?? 0)} day${Math.abs(expiryDays ?? 0) === 1 ? '' : 's'} ago`
                  : `${expiryDays} day${expiryDays === 1 ? '' : 's'} left`}
              </span>
            </span>
          )}
        </div>

        {/* Pushed to the bottom by the body's margin-top:auto, so actions line up across a row
            of cards whose text runs to different lengths. */}
        <div className="certs__card-actions">
          {hasDocument ? (
            <button
              type="button"
              className="certs__btn certs__btn--sm"
              disabled={opening}
              onClick={() => onOpenDocument(c)}
            >
              {opening ? 'Opening…' : 'View Certificate'}
            </button>
          ) : (
            <span className="certs__muted">No document</span>
          )}
          {canUpdate && (
            <button
              type="button"
              className="certs__btn certs__btn--sm certs__btn--quiet"
              onClick={() => onRecordAttempt(c)}
            >
              {passed ? 'Add Attempt' : 'Update Result'}
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

export default CertificationCard;
